import React, { useEffect, useState } from 'react'

import { useModal } from '../../providers/ModalProvider/ModalProvider'

import {
  getDynamicPricingRules,
  upsertDynamicPricingRule,
  deleteDynamicPricingRule,
  applyDynamicPricingRules,
  previewDynamicPricingRules,
  getDynamicPricingSettlement,
  updateEventDynamicPricing,
} from '../../utilities/api'

import Card from 'react-bootstrap/Card'
import Modal from 'react-bootstrap/Modal'
import Stack from 'react-bootstrap/Stack'
import Button from 'react-bootstrap/Button'
import Badge from 'react-bootstrap/Badge'
import Form from 'react-bootstrap/Form'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import Alert from 'react-bootstrap/Alert'

import { EmptyContainer } from '../EmptyContainer'
import { PageLoadingContainer } from '../PageLoadingContainer'
import { Delete } from '../ModalContent'
import DynamicPricingSettlementReport from './DynamicPricingSettlementReport'
import DynamicPricingPreviewResults from './DynamicPricingPreviewResults'

import './dynamicPricingWrapper.scss'

const TRIGGER_LABELS = {
  inventory_threshold: 'Inventory Threshold',
  time_based: 'Time Before Event',
  demand_surge: 'Demand Surge',
  manual: 'Manual',
}

const TRIGGER_BADGE_VARIANTS = {
  manual: { bg: 'warning', text: 'dark' },
  inventory_threshold: { bg: 'success' },
  demand_surge: { bg: 'danger' },
  time_based: { bg: 'dark' },
}

const ADJUSTMENT_LABELS = {
  increase_by_percent: '% Increase',
  decrease_by_percent: '% Decrease',
  increase_by_price: '$ Increase',
  decrease_by_price: '$ Decrease',
  set_price: 'Set Price',
}

const STRATEGY_OPTIONS = [
  {
    value: 'stack',
    label: 'Stack all matching rules',
    hint: 'Every matching rule applies in priority order (default).',
  },
  {
    value: 'best_match',
    label: 'Best match per scope',
    hint: 'Only the highest-priority matching event rule and PO rule apply.',
  },
  {
    value: 'manual_only',
    label: 'Manual apply only',
    hint: 'Automated hourly sweeps skip this event; use Preview/Apply in admin.',
  },
]

const TRIGGER_CONDITION_HINTS = {
  inventory_threshold: 'Requires at least one Inventory Remaining % condition.',
  time_based: 'Requires at least one Hours Until Event condition.',
  demand_surge: 'Requires at least one Sales Velocity / Hour condition.',
  manual:
    'No conditions required — fires on every apply (optional filters allowed).',
}

const TRIGGER_ALLOWED_METRICS = {
  inventory_threshold: ['inventoryRemainingPct'],
  time_based: ['hoursUntilEvent'],
  demand_surge: ['salesVelocityPerHour'],
  manual: null,
}

const DEFAULT_RULE = {
  name: '',
  enabled: true,
  scope: 'event',
  triggerType: 'inventory_threshold',
  adjustment: 'increase_by_percent',
  adjustmentValue: 10,
  floor: '',
  ceiling: '',
  priority: 0,
  conditions: {},
}

// --- Condition editor helpers ------------------------------------------------

const CONDITION_METRIC_OPTIONS = [
  {
    value: 'inventoryRemainingPct',
    label: 'Inventory Remaining %',
    hint: '0–1, e.g. 0.30 = 30%',
  },
  { value: 'hoursUntilEvent', label: 'Hours Until Event', hint: 'e.g. 48' },
  {
    value: 'salesVelocityPerHour',
    label: 'Sales Velocity / Hour',
    hint: 'e.g. 10',
  },
]

const OPERATOR_OPTIONS = [
  { value: 'lt', label: '<' },
  { value: 'lte', label: '≤' },
  { value: 'gt', label: '>' },
  { value: 'gte', label: '≥' },
]

function ConditionRow({ metric, op, value, onRemove, onChange }) {
  return (
    <Row className="align-items-center mb-2 g-2">
      <Col xs={4}>
        <Form.Select
          size="sm"
          value={metric}
          onChange={(e) => onChange('metric', e.target.value)}
        >
          {CONDITION_METRIC_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </Form.Select>
      </Col>
      <Col xs={2}>
        <Form.Select
          size="sm"
          value={op}
          onChange={(e) => onChange('op', e.target.value)}
        >
          {OPERATOR_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </Form.Select>
      </Col>
      <Col xs={4}>
        <Form.Control
          size="sm"
          type="number"
          value={value}
          onChange={(e) => onChange('value', e.target.value)}
        />
      </Col>
      <Col xs={2}>
        <Button size="sm" variant="outline-danger" onClick={onRemove}>
          Remove
        </Button>
      </Col>
    </Row>
  )
}

// Converts the flat list of rows back into the JSON conditions shape
function rowsToConditions(rows) {
  const cond = {}
  for (const row of rows) {
    if (!row.metric || row.value === '') continue
    if (!cond[row.metric]) cond[row.metric] = {}
    cond[row.metric][row.op] = Number(row.value)
  }
  return cond
}

// Converts conditions JSON into the flat list of rows
function conditionsToRows(conditions) {
  if (!conditions || typeof conditions !== 'object') return []
  const rows = []
  for (const [metric, ops] of Object.entries(conditions)) {
    for (const [op, value] of Object.entries(ops)) {
      rows.push({ metric, op, value: String(value) })
    }
  }
  return rows
}

function validateRuleConditions(triggerType, conditions) {
  const keys = Object.keys(conditions || {})

  if (triggerType === 'manual') return null

  if (keys.length === 0) {
    return (
      TRIGGER_CONDITION_HINTS[triggerType] ||
      'At least one condition is required'
    )
  }

  const allowed = TRIGGER_ALLOWED_METRICS[triggerType] || []
  const invalid = keys.filter((key) => !allowed.includes(key))
  if (invalid.length) {
    return `Invalid condition metrics for ${TRIGGER_LABELS[triggerType]}: ${invalid.join(', ')}`
  }

  return null
}

// --- Rule form ---------------------------------------------------------------

function RuleForm({ rule, pricingObjectNames, onSave, onClose }) {
  const [form, setForm] = useState({
    ...DEFAULT_RULE,
    ...rule,
    floor: rule?.floor != null ? String(rule.floor) : '',
    ceiling: rule?.ceiling != null ? String(rule.ceiling) : '',
    adjustmentValue:
      rule?.adjustmentValue != null ? String(rule.adjustmentValue) : '10',
    priority: rule?.priority != null ? String(rule.priority) : '0',
  })
  const [condRows, setCondRows] = useState(conditionsToRows(rule?.conditions))
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  const set = (key, val) => setForm((f) => ({ ...f, [key]: val }))

  const addCondRow = () =>
    setCondRows((rows) => [
      ...rows,
      { metric: 'inventoryRemainingPct', op: 'lt', value: '' },
    ])

  const updateCondRow = (idx, field, val) =>
    setCondRows((rows) =>
      rows.map((r, i) => (i === idx ? { ...r, [field]: val } : r))
    )

  const removeCondRow = (idx) =>
    setCondRows((rows) => rows.filter((_, i) => i !== idx))

  const handleSave = async () => {
    if (!form.name.trim()) {
      setError('Rule name is required')
      return
    }
    if (!form.adjustmentValue || isNaN(Number(form.adjustmentValue))) {
      setError('Adjustment value must be a number')
      return
    }

    setSaving(true)
    setError(null)
    const conditions = rowsToConditions(condRows)
    const conditionError = validateRuleConditions(form.triggerType, conditions)
    if (conditionError) {
      setError(conditionError)
      setSaving(false)
      return
    }

    try {
      await onSave({
        ...form,
        adjustmentValue: Number(form.adjustmentValue),
        floor: form.floor !== '' ? Number(form.floor) : null,
        ceiling: form.ceiling !== '' ? Number(form.ceiling) : null,
        priority: Number(form.priority) || 0,
        conditions,
      })
    } catch (err) {
      setError(err?.response?.data?.error?.message ?? 'Failed to save rule')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal.Body className="pb-4">
      <h5 className="mb-3">{rule?.id ? 'Edit Rule' : 'Add Rule'}</h5>
      {error && (
        <Alert variant="danger" className="py-2">
          {error}
        </Alert>
      )}

      <Row className="g-3 mb-3">
        <Col xs={8}>
          <Form.Group>
            <Form.Label>Rule Name</Form.Label>
            <Form.Control
              value={form.name}
              onChange={(e) => set('name', e.target.value)}
              placeholder="e.g. Low inventory surge"
            />
          </Form.Group>
        </Col>
        <Col xs={4}>
          <Form.Group>
            <Form.Label>Priority</Form.Label>
            <Form.Control
              type="number"
              value={form.priority}
              onChange={(e) => set('priority', e.target.value)}
            />
          </Form.Group>
        </Col>
      </Row>

      <Row className="g-3 mb-3">
        <Col xs={6}>
          <Form.Group>
            <Form.Label>Scope</Form.Label>
            <Form.Select
              value={form.scope}
              onChange={(e) => set('scope', e.target.value)}
            >
              <option value="event">Entire Event</option>
              <option value="pricing_object">Specific Pricing Level</option>
            </Form.Select>
          </Form.Group>
        </Col>
        {form.scope === 'pricing_object' && (
          <Col xs={6}>
            <Form.Group>
              <Form.Label>Pricing Level</Form.Label>
              <Form.Select
                value={form.pricingObjectName ?? ''}
                onChange={(e) => set('pricingObjectName', e.target.value)}
              >
                <option value="">Select a level…</option>
                {(pricingObjectNames ?? []).map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
        )}
      </Row>

      <Row className="g-3 mb-3">
        <Col xs={6}>
          <Form.Group>
            <Form.Label>Trigger Type</Form.Label>
            <Form.Select
              value={form.triggerType}
              onChange={(e) => set('triggerType', e.target.value)}
            >
              {Object.entries(TRIGGER_LABELS).map(([v, l]) => (
                <option key={v} value={v}>
                  {l}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col xs={6}>
          <Form.Group>
            <Form.Label>Enabled</Form.Label>
            <Form.Check
              type="switch"
              checked={form.enabled}
              onChange={(e) => set('enabled', e.target.checked)}
              label={form.enabled ? 'Active' : 'Paused'}
              className="mt-2"
            />
          </Form.Group>
        </Col>
      </Row>
      <p className="text-muted small mb-3">
        {TRIGGER_CONDITION_HINTS[form.triggerType]}
      </p>

      <div className="mb-3">
        <Form.Label>
          Conditions{' '}
          <small className="text-muted">
            (all must be true for rule to fire)
          </small>
        </Form.Label>
        {condRows.map((row, idx) => (
          <ConditionRow
            key={idx}
            {...row}
            onRemove={() => removeCondRow(idx)}
            onChange={(field, val) => updateCondRow(idx, field, val)}
          />
        ))}
        <Button size="sm" variant="outline-secondary" onClick={addCondRow}>
          + Add Condition
        </Button>
        {condRows.length === 0 && (
          <p className="text-muted small mt-1 mb-0">
            No conditions — rule always fires when enabled.
          </p>
        )}
      </div>

      <Row className="g-3 mb-3">
        <Col xs={6}>
          <Form.Group>
            <Form.Label>Adjustment Type</Form.Label>
            <Form.Select
              value={form.adjustment}
              onChange={(e) => set('adjustment', e.target.value)}
            >
              {Object.entries(ADJUSTMENT_LABELS).map(([v, l]) => (
                <option key={v} value={v}>
                  {l}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col xs={6}>
          <Form.Group>
            <Form.Label>Adjustment Value</Form.Label>
            <Form.Control
              type="number"
              value={form.adjustmentValue}
              onChange={(e) => set('adjustmentValue', e.target.value)}
            />
          </Form.Group>
        </Col>
      </Row>

      <Row className="g-3 mb-4">
        <Col xs={6}>
          <Form.Group>
            <Form.Label>Price Floor (min)</Form.Label>
            <Form.Control
              type="number"
              value={form.floor}
              onChange={(e) => set('floor', e.target.value)}
              placeholder="No floor"
            />
          </Form.Group>
        </Col>
        <Col xs={6}>
          <Form.Group>
            <Form.Label>Price Ceiling (max)</Form.Label>
            <Form.Control
              type="number"
              value={form.ceiling}
              onChange={(e) => set('ceiling', e.target.value)}
              placeholder="No ceiling"
            />
          </Form.Group>
        </Col>
      </Row>

      <div className="d-flex gap-2 justify-content-end mt-2">
        <Button variant="outline-secondary" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : 'Save Rule'}
        </Button>
      </div>
    </Modal.Body>
  )
}

// --- Rule card ---------------------------------------------------------------

function RuleCard({ rule, onEdit, onDelete, onToggle }) {
  const triggerBadge = TRIGGER_BADGE_VARIANTS[rule.triggerType] ?? {
    bg: 'secondary',
  }

  return (
    <div className="dynamic-pricing-rule-card">
      <div className="rule-card-header">
        <div className="rule-card-title">
          <span className="rule-name">{rule.name}</span>
          <Badge
            bg={rule.scope === 'event' ? 'primary' : 'secondary'}
            className="ms-2"
          >
            {rule.scope === 'event'
              ? 'Event-wide'
              : (rule.pricingObjectName ?? 'Level')}
          </Badge>
          <Badge bg={triggerBadge.bg} text={triggerBadge.text} className="ms-1">
            {TRIGGER_LABELS[rule.triggerType] ?? rule.triggerType}
          </Badge>
        </div>
        <div className="rule-card-actions d-flex align-items-center gap-2">
          <Form.Check
            type="switch"
            checked={rule.enabled}
            onChange={(e) => onToggle(rule, e.target.checked)}
            title={rule.enabled ? 'Pause rule' : 'Enable rule'}
          />
          <Button
            size="sm"
            variant="outline-secondary"
            onClick={() => onEdit(rule)}
          >
            Edit
          </Button>
          <Button
            size="sm"
            variant="outline-danger"
            onClick={() => onDelete(rule.id)}
          >
            Delete
          </Button>
        </div>
      </div>

      <div className="rule-card-body text-muted small">
        <span>
          <strong>Adjustment:</strong>{' '}
          {ADJUSTMENT_LABELS[rule.adjustment] ?? rule.adjustment}{' '}
          {rule.adjustmentValue}
        </span>
        {rule.floor != null && (
          <span className="ms-3">
            <strong>Floor:</strong> ${rule.floor}
          </span>
        )}
        {rule.ceiling != null && (
          <span className="ms-3">
            <strong>Ceiling:</strong> ${rule.ceiling}
          </span>
        )}
        {rule.priority !== 0 && (
          <span className="ms-3">
            <strong>Priority:</strong> {rule.priority}
          </span>
        )}
        {rule.lastAppliedAt && (
          <span className="ms-3">
            <strong>Last applied:</strong>{' '}
            {new Date(rule.lastAppliedAt).toLocaleString()}
            {rule.lastAppliedDelta != null && (
              <span
                className={
                  rule.lastAppliedDelta >= 0 ? 'text-success' : 'text-danger'
                }
              >
                {' '}
                ({rule.lastAppliedDelta >= 0 ? '+' : ''}
                {rule.lastAppliedDelta.toFixed(2)})
              </span>
            )}
          </span>
        )}
      </div>

      {rule.conditions && Object.keys(rule.conditions).length > 0 && (
        <div className="rule-card-conditions text-muted small mt-1">
          <strong>Conditions:</strong>{' '}
          {Object.entries(rule.conditions)
            .map(([metric, ops]) =>
              Object.entries(ops)
                .map(([op, val]) => `${metric} ${op} ${val}`)
                .join(' AND ')
            )
            .join(' AND ')}
        </div>
      )}
    </div>
  )
}

// --- Main wrapper ------------------------------------------------------------

export default function DynamicPricingWrapper({ event, pricingObjectNames }) {
  const { openModal, closeModal } = useModal()

  const eventUUID = event?.uuid
  const [isLoading, setIsLoading] = useState(false)
  const [rules, setRules] = useState([])
  const [applying, setApplying] = useState(false)
  const [previewing, setPreviewing] = useState(false)
  const [applyResult, setApplyResult] = useState(null)
  const [previewResult, setPreviewResult] = useState(null)
  const [settlementData, setSettlementData] = useState(null)
  const [isLoadingSettlement, setIsLoadingSettlement] = useState(false)
  const [dynamicEnabled, setDynamicEnabled] = useState(
    Boolean(event?.dynamicPricingEnabled)
  )
  const [strategy, setStrategy] = useState(
    event?.dynamicPricingStrategy || 'stack'
  )
  const [savingSettings, setSavingSettings] = useState(false)
  const [settingsMessage, setSettingsMessage] = useState(null)

  useEffect(() => {
    setDynamicEnabled(Boolean(event?.dynamicPricingEnabled))
    setStrategy(event?.dynamicPricingStrategy || 'stack')
  }, [event?.dynamicPricingEnabled, event?.dynamicPricingStrategy])

  useEffect(() => {
    if (!eventUUID) return
    setIsLoading(true)
    loadRules().finally(() => setIsLoading(false))
  }, [eventUUID])

  const loadSettlement = async () => {
    if (!eventUUID || !event?.organizationUUID) return
    setIsLoadingSettlement(true)
    try {
      const res = await getDynamicPricingSettlement(
        event.organizationUUID,
        eventUUID
      )
      setSettlementData(res?.data || null)
    } catch (err) {
      console.error('Failed to load dynamic pricing settlement report', err)
      setSettlementData(null)
    } finally {
      setIsLoadingSettlement(false)
    }
  }

  useEffect(() => {
    if (!eventUUID || !event?.organizationUUID) return
    loadSettlement()
  }, [eventUUID, event?.organizationUUID])

  const loadRules = async () => {
    const res = await getDynamicPricingRules(eventUUID)
    setRules(res.data?.data ?? [])
  }

  const openRuleForm = (rule = null) => {
    openModal(
      RuleForm,
      {
        rule: rule
          ? { ...rule }
          : {
              ...DEFAULT_RULE,
              eventUUID,
              organizationUUID: event?.organizationUUID,
            },
        pricingObjectNames,
        onSave: async (data) => {
          await upsertDynamicPricingRule({
            ...data,
            eventUUID,
            organizationUUID: event?.organizationUUID,
          })
          await loadRules()
          closeModal()
        },
        onClose: closeModal,
      },
      { className: 'modal--lg', scrollable: true, fullscreen: true }
    )
  }

  const handleDelete = (id) => {
    openModal(
      Delete,
      {
        entity: 'dynamic pricing rule',
        id,
        handleDelete: async (ruleId) => {
          await deleteDynamicPricingRule(ruleId)
          setRules((prev) => prev.filter((r) => r.id !== ruleId))
        },
        onClose: closeModal,
      },
      { className: '', scrollable: false }
    )
  }

  const handleToggle = async (rule, enabled) => {
    setRules((prev) =>
      prev.map((r) => (r.id === rule.id ? { ...r, enabled } : r))
    )
    await upsertDynamicPricingRule({ ...rule, enabled })
  }

  const handlePreview = async () => {
    setPreviewing(true)
    setApplyResult(null)
    try {
      const res = await previewDynamicPricingRules(
        eventUUID,
        event?.organizationUUID
      )
      setPreviewResult(res.data?.data ?? null)
    } catch (err) {
      setPreviewResult(null)
      setApplyResult({
        success: false,
        message: err?.response?.data?.error?.message ?? 'Preview failed',
      })
    } finally {
      setPreviewing(false)
    }
  }

  const handleApply = async () => {
    setApplying(true)
    setApplyResult(null)
    try {
      const res = await applyDynamicPricingRules(eventUUID)
      setApplyResult({ success: true, ...res.data?.data })
      await loadRules()
      await loadSettlement()
    } catch (err) {
      setApplyResult({
        success: false,
        message: err?.response?.data?.error?.message ?? 'Apply failed',
      })
    } finally {
      setApplying(false)
    }
  }

  const handleSaveSettings = async () => {
    if (!event?.id) return
    setSavingSettings(true)
    setSettingsMessage(null)
    try {
      await updateEventDynamicPricing(event.id, dynamicEnabled, strategy)
      setSettingsMessage({ success: true, text: 'Event settings saved.' })
    } catch (err) {
      setSettingsMessage({
        success: false,
        text:
          err?.response?.data?.error?.message ??
          'Failed to save event settings',
      })
    } finally {
      setSavingSettings(false)
    }
  }

  if (isLoading) return <PageLoadingContainer style="without-sidebar" />

  return (
    <section
      className="spacer-sm dynamic-pricing-wrapper"
      data-testid="dynamic-pricing-wrapper"
    >
      <header className="section-heading--flex section-header-sm">
        <div className="section-heading">
          <h1>Dynamic Pricing Rules</h1>
          <p className="section-header-desc">
            Automatically adjust pricing level prices based on inventory, time,
            or demand.
          </p>
        </div>
        <div className="d-flex gap-2">
          <Button
            variant="outline-secondary"
            size="lg"
            onClick={handlePreview}
            disabled={previewing || applying || rules.length === 0}
          >
            {previewing ? 'Previewing…' : 'Preview Rules'}
          </Button>
          <Button
            variant="outline-primary"
            size="lg"
            onClick={handleApply}
            disabled={applying || previewing || rules.length === 0}
          >
            {applying ? 'Applying…' : 'Apply Rules Now'}
          </Button>
          <Button size="lg" onClick={() => openRuleForm()}>
            Add Rule
          </Button>
        </div>
      </header>

      <Card body className="card--md card--light mb-3">
        <Row className="g-3 align-items-end">
          <Col md={4}>
            <Form.Group>
              <Form.Label>Dynamic pricing</Form.Label>
              <Form.Check
                type="switch"
                checked={dynamicEnabled}
                onChange={(e) => setDynamicEnabled(e.target.checked)}
                label={dynamicEnabled ? 'Enabled for this event' : 'Disabled'}
              />
            </Form.Group>
          </Col>
          <Col md={5}>
            <Form.Group>
              <Form.Label>Strategy</Form.Label>
              <Form.Select
                value={strategy}
                onChange={(e) => setStrategy(e.target.value)}
              >
                {STRATEGY_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>
              <Form.Text className="text-muted">
                {
                  STRATEGY_OPTIONS.find((option) => option.value === strategy)
                    ?.hint
                }
              </Form.Text>
            </Form.Group>
          </Col>
          <Col md={3} className="d-flex justify-content-md-end">
            <Button
              variant="outline-primary"
              onClick={handleSaveSettings}
              disabled={savingSettings}
            >
              {savingSettings ? 'Saving…' : 'Save Settings'}
            </Button>
          </Col>
        </Row>
        {settingsMessage && (
          <Alert
            variant={settingsMessage.success ? 'success' : 'danger'}
            className="mt-3 mb-0 py-2"
            dismissible
            onClose={() => setSettingsMessage(null)}
          >
            {settingsMessage.text}
          </Alert>
        )}
      </Card>

      {applyResult && (
        <Alert
          variant={applyResult.success ? 'success' : 'danger'}
          dismissible
          onClose={() => setApplyResult(null)}
          className="mb-3"
        >
          {applyResult.success
            ? `Applied to ${applyResult.applied ?? 0} pricing level(s). ${applyResult.skipped ? `${applyResult.skipped} skipped.` : ''}`
            : applyResult.message}
        </Alert>
      )}

      {rules.length > 0 ? (
        <Card body className="card--md card--light">
          <Stack as="ul" gap={3}>
            {rules.map((rule) => (
              <RuleCard
                key={rule.id}
                rule={rule}
                onEdit={openRuleForm}
                onDelete={handleDelete}
                onToggle={handleToggle}
              />
            ))}
          </Stack>
        </Card>
      ) : (
        <Card body>
          <EmptyContainer style="center lg">
            <p>No dynamic pricing rules added</p>
          </EmptyContainer>
        </Card>
      )}

      {previewResult && (
        <div className="mt-4">
          <DynamicPricingPreviewResults data={previewResult} />
        </div>
      )}

      {previewResult?.projectedSettlement && (
        <div className="mt-4">
          <header className="section-header-sm mb-3">
            <div className="section-heading">
              <h2 className="h4 mb-1">Projected Settlement</h2>
              <p className="section-header-desc mb-0">
                Estimated lift and BT fees if preview prices applied to tickets
                already sold per pricing level snapshots.
              </p>
            </div>
          </header>
          <DynamicPricingSettlementReport
            data={previewResult.projectedSettlement}
            variant="preview"
            organizationName={event?.organization?.name || ''}
          />
        </div>
      )}

      <div className="mt-4">
        <header className="section-header-sm mb-3">
          <div className="section-heading">
            <h2 className="h4 mb-1">Actual Settlement</h2>
            <p className="section-header-desc mb-0">
              Price lift earnings from completed dynamic pricing sales, with BT
              fees from your organization fee config.
            </p>
          </div>
        </header>
        <DynamicPricingSettlementReport
          data={settlementData}
          isLoading={isLoadingSettlement}
          organizationName={event?.organization?.name || ''}
        />
      </div>
    </section>
  )
}
